import {
  useState,
  lazy,
  Suspense,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { Snackbar } from '../../../../shared/components/Snackbar';
import { useAuthViewport } from './AuthTemplate/useAuthViewport';
import RouteLoader from '@/app/routes/Loader';
import { Loader } from '@/shared/components/Loader';

const AuthTemplate = lazy(() => import('./AuthTemplate/index'));
const LoginForm = lazy(() => import('./loginForm'));
const OtpForm = lazy(() => import('./otpForm'));

type AuthView = 'login' | 'otp';

const LoginPage = () => {
  const { isTablet } = useAuthViewport();
  const [currentView, setCurrentView] = useState<AuthView>('login');
  const [userEmail, setUserEmail] = useState('');
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  // Captured once at mount so the Suspense fallback keeps showing
  // "Wylogowywanie..." even after the useEffect below strips ?status=logged-out
  // from the URL while the AuthTemplate lazy chunk is still loading.
  const [isLogoutFlow] = useState(
    () =>
      new URLSearchParams(window.location.search).get('status') === 'logged-out'
  );

  // Detect logout status from URL
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'logged-out') {
      setSnackbarMessage('Zostałeś(-aś) wylogowany(-a).');
      setShowSuccessSnackbar(true);

      // Clear the parameter from the URL without reloading
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleLoginSuccess = useCallback((email: string) => {
    setUserEmail(email);
    setSnackbarMessage(
      'Jednorazowy kod został wysłany na Twój adres e-mail. Sprawdź skrzynkę odbiorczą.'
    );
    setShowSuccessSnackbar(true);
    setCurrentView('otp');
  }, []);

  const handleBackToLogin = useCallback(() => {
    setCurrentView('login');
  }, []);

  const handleSnackbarClose = useCallback(() => {
    setShowSuccessSnackbar(false);
  }, []);

  // Memoize FormComponent to prevent unnecessary re-creation
  const FormComponent = useMemo(() => {
    return currentView === 'login'
      ? () => (
          <LoginForm onLoginSuccess={handleLoginSuccess} isTablet={isTablet} />
        )
      : () => (
          <OtpForm
            email={userEmail}
            onBack={handleBackToLogin}
            isTablet={isTablet}
          />
        );
  }, [currentView, userEmail, handleLoginSuccess, handleBackToLogin, isTablet]);

  return (
    <>
      <Suspense
        fallback={
          <div className="flex items-center justify-center h-[100vh]">
            {isLogoutFlow ? (
              <Loader text="Wylogowywanie..." />
            ) : (
              <RouteLoader />
            )}
          </div>
        }
      >
        <AuthTemplate FormComponent={FormComponent} />
      </Suspense>

      <Snackbar
        open={showSuccessSnackbar}
        message={snackbarMessage}
        variant="success"
        onClose={handleSnackbarClose}
        autoHideDuration={15000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default LoginPage;
